import BoltIcon from "@mui/icons-material/Bolt";
import FactoryIcon from "@mui/icons-material/Factory";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { Box, Skeleton } from "@mui/material";
import { useState } from "react";
import { EMPTY_FLEET_SELECTION, type FleetSelection } from "../api/dashboard";
import FleetFilterBar from "../components/FleetFilterBar";
import FleetMap from "../components/FleetMap";
import FleetRiskDonut from "../components/FleetRiskDonut";
import KpiCard from "../components/KpiCard";
import PlantRiskTable from "../components/PlantRiskTable";
import { useFleetOverview } from "../hooks/usePlantOverview";
import PageHeader from "../layout/PageHeader";
import { mw } from "../utils/format";

export default function FleetOverviewPage() {
  const [selection, setSelection] = useState<FleetSelection>(
    EMPTY_FLEET_SELECTION,
  );
  const { data: fleet } = useFleetOverview(selection);

  return (
    <Box>
      <PageHeader
        title="SPOF Portfolio Overview"
        subtitle="Fleet-wide single point of failure exposure, risk profile and mitigation potential."
        trail={[{ label: "Home" }, { label: "Portfolio overview" }]}
      />

      {!fleet ? (
        <Skeleton variant="rounded" height={520} />
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <FleetFilterBar
            options={fleet.filters}
            selection={selection}
            onChange={setSelection}
          />

          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
                xl: "repeat(5, 1fr)",
              },
            }}
          >
            <KpiCard
              label="Plants Assessed"
              value={fleet.kpis.plants_assessed}
              color="#1B6FD4"
              icon={FactoryIcon}
            />
            <KpiCard
              label="Confirmed SPOFs"
              value={fleet.kpis.confirmed_spofs}
              color="#E53935"
              icon={WarningAmberIcon}
            />
            <KpiCard
              label="High & Severe"
              value={fleet.kpis.high_and_severe}
              color="#F57C00"
              icon={PriorityHighIcon}
            />
            <KpiCard
              label="Baseline Exposure"
              value={mw(fleet.kpis.baseline_exposure_mw)}
              suffix=" MW"
              color="#1AA5A0"
              icon={BoltIcon}
            />
            <KpiCard
              label="Potential Risk Reduction"
              value={fleet.kpis.potential_risk_reduction_percentage}
              suffix="%"
              color="#43A047"
              icon={TrendingDownIcon}
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: { xs: "1fr", lg: "3fr 2fr" },
            }}
          >
            <FleetMap plants={fleet.plants} />
            <FleetRiskDonut buckets={fleet.risk_distribution} />
          </Box>

          <PlantRiskTable plants={fleet.plants} />
        </Box>
      )}
    </Box>
  );
}
